import './AddStudentsToClass.scss';
import '../../OrganizationModals.scss';
import { Checkbox, Modal, Table, TableBody, TableCell, TableHead, TableRow } from '@trackman/web-shared-components';
import Subtitle from 'components/TextComps/Subtitle';
import { ReactComponent as AddStudentsIcon } from 'images/icons/AddUsers.svg';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NoStudentsFound } from '../../Students/NoStudentsFound/NoStudentsFound';
import { useOrganizationStore } from 'stores/Organization/OrganizationStore';
import { StudentsSearchInput } from '../../StudentsSearch/StudentsSearch';
import { t } from 'i18next';
import { useGetInvites } from 'queryHooks/Organization/Invites/useGetInvites';
import { useGetStudents } from 'queryHooks/Organization/Students/useGetStudents';
import { handleSearchStudent } from 'utilities/search-helper';
import { OrganizationTableInitials } from '../../OrganizationTableInitials/OrganizationTableInitials';
import { getFilteredEntriesToAdd } from 'utilities/addStudentsToClassFiltersHelper';
import {
  AddStudentsToClassModalProps,
  AddStudentsToClassProps,
  StudentsAndInvitesTableEntry,
} from 'types/organization/addStudentsToClassUI';
import { useAddSelectedStudentsToClass } from 'queryHooks/Organization/Classes/useAddSelectedStudentsToClass';

export const AddStudentsToClass = ({ isOpen, selectedClassId, close }: AddStudentsToClassProps) => {
  const selectedOrganizationId = useOrganizationStore((state) => state.selectedOrganizationId);

  const { data: studentList, isPending: isStudentListPending } = useGetStudents(selectedOrganizationId);
  const { data: inviteList, isPending: isInviteListPending } = useGetInvites(selectedOrganizationId);

  const [selectedStudentIds, setSelectedStudentIds] = useState<string[]>([]);
  const [selectedInviteIds, setSelectedInviteIds] = useState<string[]>([]);

  const addStudentsToClass = useAddSelectedStudentsToClass(selectedClassId, selectedOrganizationId, {
    onSuccess: () => close(),
  });

  const confirm = () => addStudentsToClass.mutate({ inviteIds: selectedInviteIds, studentIds: selectedStudentIds });

  return (
    <>
      {!isStudentListPending && !isInviteListPending ? (
        <AddStudentsToClassModal
          isOpen={isOpen}
          selectedClassId={selectedClassId}
          selectedInviteIds={selectedInviteIds}
          selectedStudentIds={selectedStudentIds}
          studentList={studentList}
          inviteList={inviteList}
          setSelectedInviteIds={setSelectedInviteIds}
          setSelectedStudentIds={setSelectedStudentIds}
          close={close}
          confirm={confirm}
        />
      ) : (
        <></>
      )}
    </>
  );
};

const AddStudentsToClassModal = ({
  isOpen,
  selectedInviteIds,
  selectedStudentIds,
  studentList,
  inviteList,
  setSelectedInviteIds,
  setSelectedStudentIds,
  close,
  confirm,
}: AddStudentsToClassModalProps) => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<string>('');

  const studentsTableEntries: StudentsAndInvitesTableEntry[] | undefined = studentList?.map((student) => ({
    id: student.id,
    name: student.name,
    email: student.email,
    classNames: student.classes?.map((c) => c.name).join(', ') ?? null,
  }));

  const invitesTableEntries: StudentsAndInvitesTableEntry[] | undefined = inviteList?.map((invite) => ({
    id: invite.id,
    name: invite.name ?? '',
    email: invite.email,
    classNames: invite?.classes?.map((c) => c.name).join(', ') ?? null,
  }));

  const filteredStudentsTableEntries = studentsTableEntries && handleSearchStudent(filter, studentsTableEntries);
  const filteredInvitesTableEntries = invitesTableEntries && handleSearchStudent(filter, invitesTableEntries);
  const hasOrganizationStudentOrInvites = (studentList && studentList.length > 0) || (inviteList && inviteList.length > 0);

  return (
    <Modal
      open={isOpen}
      onClose={() => close()}
      className='organization-modal add-students-to-class'
      title={t('AddStudents', { ns: 'studentsPage' })}
      titleIcon={<AddStudentsIcon />}
      cancelBtn={{
        label: t('Cancel', { ns: 'general' }),
        onClick: () => close(),
        className: 'styled-button secondary',
      }}
      confirmBtn={{
        label: t('AddStudents', { ns: 'studentsPage' }),
        onClick: () => confirm(),
        disabled: selectedStudentIds.length === 0 && selectedInviteIds.length === 0,
      }}
    >
      <div className='modal-body'>
        <div className='modal-subtitle'>
          <Subtitle>{t('SelectWhichStudentsShouldBeAddedToThisClass', { ns: 'studentsPage' })}</Subtitle>
          <span>{t('PickByBrowsingTheTableOrSearch', { ns: 'studentsPage' })}</span>
        </div>

        {hasOrganizationStudentOrInvites ? (
          <>
            <div className='student-search-input-wrapper'>
              <StudentsSearchInput label={t('SearchForStudentsAndInvites', { ns: 'studentsPage' })} value={filter} onChange={setFilter} />
            </div>
            <StudentsAndInvitesTable
              selectedInviteIds={selectedInviteIds}
              selectedStudentIds={selectedStudentIds}
              filteredInvites={filteredInvitesTableEntries}
              filteredStudents={filteredStudentsTableEntries}
              setSelectedInviteIds={setSelectedInviteIds}
              setSelectedStudentIds={setSelectedStudentIds}
            />
            <div className='modal-summary'>
              <span>
                {t('NumberOfStudentsSelected', { ns: 'studentsPage' })} {selectedInviteIds.length + selectedStudentIds.length}
              </span>
            </div>
          </>
        ) : (
          <NoStudentsFound />
        )}
      </div>
    </Modal>
  );
};

const StudentsAndInvitesTable = ({
  selectedStudentIds,
  selectedInviteIds,
  filteredStudents,
  filteredInvites,
  setSelectedInviteIds,
  setSelectedStudentIds,
}) => {
  const allFilteredEntriesNumber = filteredStudents.length + filteredInvites.length;
  const numberOfSelectedStudentIdsWithinFilteredEntries = filteredStudents.filter((s) => selectedStudentIds.includes(s.id)).length;
  const numberOfSelectedInviteIdsWithinFilteredEntries = filteredInvites.filter((s) => selectedInviteIds.includes(s.id)).length;

  const handleSelectAll = () => {
    if (numberOfSelectedStudentIdsWithinFilteredEntries + numberOfSelectedInviteIdsWithinFilteredEntries === allFilteredEntriesNumber) {
      setSelectedInviteIds(selectedInviteIds.filter((id) => !filteredInvites.find((invite) => invite.id === id)));
      setSelectedStudentIds(selectedStudentIds.filter((id) => !filteredStudents.find((student) => student.id === id)));
    } else {
      const filteredInvitesToAdd = getFilteredEntriesToAdd(filteredInvites, selectedInviteIds);
      const filteredStudentsToAdd = getFilteredEntriesToAdd(filteredStudents, selectedStudentIds);
      setSelectedInviteIds([...selectedInviteIds, ...filteredInvitesToAdd.map((invite) => invite.id)]);
      setSelectedStudentIds([...selectedStudentIds, ...filteredStudentsToAdd.map((student) => student.id)]);
    }
  };

  const handleSelectAllStudentEntries = () => {
    if (numberOfSelectedStudentIdsWithinFilteredEntries === filteredStudents.length) {
      setSelectedStudentIds(selectedStudentIds.filter((id) => !filteredStudents.find((student) => student.id === id)));
    } else {
      const filteredStudentsToAdd = getFilteredEntriesToAdd(filteredStudents, selectedStudentIds);
      setSelectedStudentIds([...selectedStudentIds, ...filteredStudentsToAdd.map((student) => student.id)]);
    }
  };

  const handleSelectAllInviteEntries = () => {
    if (numberOfSelectedInviteIdsWithinFilteredEntries === filteredInvites.length) {
      setSelectedInviteIds(selectedInviteIds.filter((id) => !filteredInvites.find((invite) => invite.id === id)));
    } else {
      const filteredInvitesToAdd = getFilteredEntriesToAdd(filteredInvites, selectedInviteIds);
      setSelectedInviteIds([...selectedInviteIds, ...filteredInvitesToAdd.map((invite) => invite.id)]);
    }
  };

  const handleSelectStudent = (id: string) => {
    if (selectedStudentIds.includes(id)) {
      setSelectedStudentIds(selectedStudentIds.filter((selectedId) => selectedId !== id));
    } else {
      setSelectedStudentIds([...selectedStudentIds, id]);
    }
  };

  const handleSelectInvite = (id: string) => {
    if (selectedInviteIds.includes(id)) {
      setSelectedInviteIds(selectedInviteIds.filter((selectedId) => selectedId !== id));
    } else {
      setSelectedInviteIds([...selectedInviteIds, id]);
    }
  };

  return (
    <div className='modal-table-container'>
      <Table sx={{ minWidth: 750 }} aria-labelledby='studentsTable'>
        <TableHead>
          <TableRow>
            <TableCell padding='checkbox'>
              <Checkbox
                color='primary'
                indeterminate={
                  numberOfSelectedStudentIdsWithinFilteredEntries + numberOfSelectedInviteIdsWithinFilteredEntries > 0 &&
                  numberOfSelectedStudentIdsWithinFilteredEntries + numberOfSelectedInviteIdsWithinFilteredEntries <
                    allFilteredEntriesNumber
                }
                checked={
                  allFilteredEntriesNumber > 0 &&
                  numberOfSelectedStudentIdsWithinFilteredEntries + numberOfSelectedInviteIdsWithinFilteredEntries ===
                    allFilteredEntriesNumber
                }
                onChange={handleSelectAll}
                label={undefined}
              />
            </TableCell>
            <TableCell sx={{ width: 100 }}></TableCell>
            <TableCell sx={{ width: 250 }}>{t('Name', { ns: 'studentsPage' }) as string}</TableCell>
            <TableCell sx={{ width: 250 }}>{t('Classes', { ns: 'studentsPage' }) as string}</TableCell>
            <TableCell sx={{ width: 250 }}>{t('Email', { ns: 'studentsPage' }) as string}</TableCell>
          </TableRow>
        </TableHead>
      </Table>
      {filteredStudents.length ? (
        <StudentsAndInvitesSubTable
          entries={filteredStudents}
          selectedIds={selectedStudentIds}
          numberOfSelectedIdsWithinFilteredEntries={numberOfSelectedStudentIdsWithinFilteredEntries}
          subTableHeader={t('StudentsHeader', { ns: 'studentsPage' })}
          handleSelectEntry={(id: string) => handleSelectStudent(id)}
          handleSelectAllSubTableEntries={handleSelectAllStudentEntries}
        />
      ) : (
        <></>
      )}
      {filteredInvites.length ? (
        <StudentsAndInvitesSubTable
          entries={filteredInvites}
          selectedIds={selectedInviteIds}
          numberOfSelectedIdsWithinFilteredEntries={numberOfSelectedInviteIdsWithinFilteredEntries}
          subTableHeader={t('Invites', { ns: 'organizationPage' })}
          handleSelectEntry={(id: string) => handleSelectInvite(id)}
          handleSelectAllSubTableEntries={handleSelectAllInviteEntries}
        />
      ) : (
        <></>
      )}
    </div>
  );
};

const StudentsAndInvitesSubTable = ({
  entries,
  selectedIds,
  numberOfSelectedIdsWithinFilteredEntries,
  subTableHeader,
  handleSelectEntry,
  handleSelectAllSubTableEntries,
}) => {
  return (
    <Table sx={{ minWidth: 750 }} aria-labelledby='studentsTable'>
      <TableHead>
        <TableRow className='sub-table-head-row'>
          <TableCell padding='checkbox'>
            <Checkbox
              color='primary'
              indeterminate={numberOfSelectedIdsWithinFilteredEntries > 0 && numberOfSelectedIdsWithinFilteredEntries < entries.length}
              checked={numberOfSelectedIdsWithinFilteredEntries === entries.length}
              onChange={() => handleSelectAllSubTableEntries()}
              label={undefined}
            />
          </TableCell>
          <TableCell sx={{ width: 100 }}></TableCell>
          <TableCell sx={{ width: 250 }}>{subTableHeader}</TableCell>
          <TableCell sx={{ width: 250 }}></TableCell>
          <TableCell sx={{ width: 250 }}></TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {entries.map((entry) => (
          <SubTableEntry
            key={entry.id}
            isItemSelected={selectedIds.includes(entry.id)}
            tableEntry={entry}
            handleSelectEntry={() => handleSelectEntry(entry.id)}
          />
        ))}
      </TableBody>
    </Table>
  );
};

const SubTableEntry = ({ isItemSelected, tableEntry, handleSelectEntry }) => {
  const selectEntry = (event) => {
    event.stopPropagation();
    handleSelectEntry();
  };

  return (
    <TableRow
      hover
      onClick={(event) => selectEntry(event)}
      role='checkbox'
      aria-checked={isItemSelected}
      tabIndex={-1}
      selected={isItemSelected}
      sx={{ cursor: 'pointer' }}
    >
      <TableCell padding='checkbox'>
        <Checkbox color='primary' checked={isItemSelected} label={undefined} />
      </TableCell>
      <TableCell className='initials-cell'>
        <OrganizationTableInitials name={tableEntry.name} />
      </TableCell>
      <TableCell>{tableEntry.name}</TableCell>
      <TableCell>{tableEntry.classNames}</TableCell>
      <TableCell>{tableEntry.email}</TableCell>
    </TableRow>
  );
};
